import Avatar from '@ui/Avatar'
import Button from '@ui/Button'
import Card from '@ui/Card'
import Field from '@ui/Field'
import { IconChevRight, IconLogout } from '@ui/icons'
import Input from '@ui/Input'
import Pill from '@ui/Pill'
import { DisplayRow, FormShell, SectionShell } from '@ui/SectionShell'
import Select from '@ui/Select'
import Sheet from '@ui/Sheet'
import { type ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  MeDocument,
  PasswordStatusEnum,
  useMySignInStatusQuery,
  useUpdateMyProfileMutation,
} from '@/__generated__/hooks'
import { getIdpConfig } from '@/auth/idp'
import PasswordSheet, {
  type PasswordSheetMode,
} from '@/components/PasswordSheet'
import { type ReauthMethod } from '@/components/ReauthForm'
import RemovePasswordSheet from '@/components/RemovePasswordSheet'
import TwoFactorSheet, {
  type TwoFactorIntent,
} from '@/components/TwoFactorSheet'
import { useCurrentUser } from '@/contexts/CurrentUserProvider'
import { SUPPORTED_LANGUAGES } from '@/i18n'

// Everything here is about the person, not an account: switching account
// changes nothing on this page.
const Profile = () => {
  const { t } = useTranslation()
  const user = useCurrentUser()
  const email = user.email ?? ''

  return (
    <section className="flex flex-col gap-5 pt-4 sm:pt-6 max-w-[640px]">
      <header className="flex items-center gap-3">
        <Avatar name={user.name} size="md" />
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-ink-900 truncate">
            {user.name}
          </h1>
          <p className="text-sm text-ink-500 truncate">{email}</p>
        </div>
      </header>

      <AboutYou />
      <SignIn email={email} />
      <Language />

      <Button
        variant="secondary"
        fullWidth
        iconLeft={<IconLogout size={16} />}
        onClick={() => window.location.assign('/auth/logout')}
      >
        {t('account-switcher.log-out')}
      </Button>
    </section>
  )
}

const AboutYou = () => {
  const { t } = useTranslation()
  const user = useCurrentUser()
  const nameFromEmail =
    Boolean(user.email) && user.name === user.email?.split('@')[0]

  return (
    <SectionShell
      titleKey="profile.about.title"
      locked={false}
      renderDisplay={() => (
        <div className="flex flex-col gap-1">
          <DisplayRow labelKey="profile.about.name" value={user.name} />
          {nameFromEmail && (
            <p className="text-xs text-ink-500">
              {t('profile.about.name-from-email')}
            </p>
          )}
        </div>
      )}
      renderForm={(onDone) => <NameForm onDone={onDone} />}
    />
  )
}

const NameForm = ({ onDone }: { onDone: () => void }) => {
  const { t } = useTranslation()
  const user = useCurrentUser()
  const [name, setName] = useState(user.name)
  const [error, setError] = useState<string | null>(null)
  const [update, { loading }] = useUpdateMyProfileMutation({
    refetchQueries: [{ query: MeDocument }],
    awaitRefetchQueries: true,
  })

  const onSave = async () => {
    if (!name.trim()) {
      setError(t('profile.about.name-required'))
      return
    }
    setError(null)
    try {
      await update({ variables: { input: { name: name.trim() } } })
      onDone()
    } catch {
      setError(t('auth.password.errors.unknown'))
    }
  }

  return (
    <FormShell
      onSave={onSave}
      loading={loading}
      error={error}
      onCancel={onDone}
    >
      <Field
        label={t('profile.about.name')}
        hint={t('profile.about.name-hint')}
      >
        <Input
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
          required
        />
      </Field>
    </FormShell>
  )
}

type PasswordAction = PasswordSheetMode | 'choice' | 'remove'

/** Provider ids Identity Platform reports, with the name to show. */
const PROVIDER_NAMES: Record<string, string> = { 'google.com': 'Google' }

const SignIn = ({ email }: { email: string }) => {
  const { t } = useTranslation()
  const [idpEnabled, setIdpEnabled] = useState(false)
  const { data, refetch } = useMySignInStatusQuery({ skip: !idpEnabled })
  const [passwordSheet, setPasswordSheet] = useState<PasswordAction | null>(
    null
  )
  const [twoFactorSheet, setTwoFactorSheet] = useState<TwoFactorIntent | null>(
    null
  )

  useEffect(() => {
    let live = true
    getIdpConfig().then((config) => live && setIdpEnabled(config !== null))
    return () => {
      live = false
    }
  }, [])

  const status = data?.mySignInStatus
  const password = status?.password
  const twoFactor = status?.twoFactor ?? false
  const providers = (status?.providers ?? []).filter((id) => PROVIDER_NAMES[id])
  const hasActivePassword = password === PasswordStatusEnum.Active
  // 2FA needs a way to prove it's you before changing it: the password, or
  // Google again. A session from before the move to Identity Platform has
  // neither on record until the next sign-in.
  let reauth: ReauthMethod | null = null
  if (hasActivePassword) reauth = 'password'
  else if (providers.includes('google.com')) reauth = 'google'

  return (
    <Card padding="none">
      <h2 className="font-display font-bold text-base text-ink-900 px-4 pt-4 pb-2">
        {t('profile.sign-in.title')}
      </h2>
      <ul className="flex flex-col">
        <SignInRow
          label={t('profile.sign-in.email')}
          detail={
            <>
              <span className="block text-ink-900">{email}</span>
              {t('profile.sign-in.email-hint')}
            </>
          }
        />
        {providers.map((id) => (
          <SignInRow
            key={id}
            label={PROVIDER_NAMES[id]}
            pill={<Pill tone="success">{t('profile.sign-in.connected')}</Pill>}
          />
        ))}
        {idpEnabled && password && (
          <PasswordRow
            status={password}
            email={email}
            googleConnected={providers.includes('google.com')}
            onOpen={setPasswordSheet}
          />
        )}
        {idpEnabled && reauth && (
          <SignInRow
            label={t('two-factor.title')}
            detail={t('two-factor.scope')}
            pill={
              <Pill tone={twoFactor ? 'success' : 'neutral'}>
                {twoFactor ? t('two-factor.on') : t('two-factor.off')}
              </Pill>
            }
            onClick={() => setTwoFactorSheet(twoFactor ? 'disable' : 'enable')}
          />
        )}
      </ul>
      {idpEnabled && !providers.includes('google.com') && (
        <p className="text-xs text-ink-500 px-4 pb-4 pt-1">
          {t('profile.sign-in.other-methods', { email })}
        </p>
      )}

      {passwordSheet === 'choice' && (
        <PasswordChoiceSheet
          onClose={() => setPasswordSheet(null)}
          onPick={setPasswordSheet}
        />
      )}
      {(passwordSheet === 'create' || passwordSheet === 'change') && (
        <PasswordSheet
          open
          mode={passwordSheet}
          email={email}
          googleConnected={providers.includes('google.com')}
          onClose={() => setPasswordSheet(null)}
          onChanged={() => refetch()}
        />
      )}
      {passwordSheet === 'remove' && (
        <RemovePasswordSheet
          open
          email={email}
          twoFactor={twoFactor}
          unverified={password === PasswordStatusEnum.Unverified}
          onClose={() => setPasswordSheet(null)}
          onChanged={() => refetch()}
        />
      )}
      {twoFactorSheet && reauth && (
        <TwoFactorSheet
          open
          intent={twoFactorSheet}
          email={email}
          reauth={reauth}
          methods={{
            password: hasActivePassword,
            google: providers.includes('google.com'),
          }}
          onClose={() => setTwoFactorSheet(null)}
          onChanged={() => refetch()}
        />
      )}
    </Card>
  )
}

const PasswordRow = ({
  status,
  email,
  googleConnected,
  onOpen,
}: {
  status: PasswordStatusEnum
  email: string
  /** Removing the password is only offered when Google can take over. */
  googleConnected: boolean
  onOpen: (action: PasswordAction) => void
}) => {
  const { t } = useTranslation()
  if (status === PasswordStatusEnum.None) {
    return (
      <SignInRow
        label={t('profile.sign-in.password')}
        detail={t('profile.sign-in.password-none-detail')}
        pill={<Pill tone="neutral">{t('profile.sign-in.password-none')}</Pill>}
        onClick={() => onOpen('create')}
      />
    )
  }
  if (status === PasswordStatusEnum.Unverified) {
    return (
      <SignInRow
        label={t('profile.sign-in.password')}
        detail={t('profile.sign-in.password-unverified-detail', { email })}
        pill={
          <Pill tone="warn">{t('profile.sign-in.password-unverified')}</Pill>
        }
        onClick={googleConnected ? () => onOpen('remove') : undefined}
      />
    )
  }
  return (
    <SignInRow
      label={t('profile.sign-in.password')}
      detail={
        googleConnected
          ? t('profile.sign-in.password-manage')
          : t('profile.sign-in.change-password')
      }
      pill={<Pill tone="success">{t('profile.sign-in.password-active')}</Pill>}
      onClick={() => onOpen(googleConnected ? 'choice' : 'change')}
    />
  )
}

/** Change or remove, for people who also sign in with Google. */
const PasswordChoiceSheet = ({
  onClose,
  onPick,
}: {
  onClose: () => void
  onPick: (action: 'change' | 'remove') => void
}) => {
  const { t } = useTranslation()
  return (
    <Sheet open onClose={onClose} title={t('profile.sign-in.password')}>
      <ul className="flex flex-col -mx-4 border-b border-ink-100">
        <SignInRow
          label={t('profile.sign-in.change-password')}
          onClick={() => onPick('change')}
        />
        <SignInRow
          label={t('profile.remove-password.title')}
          labelTone="danger"
          detail={t('profile.remove-password.choice-detail')}
          onClick={() => onPick('remove')}
        />
      </ul>
    </Sheet>
  )
}

/** One row of the sign-in list; the whole row is the tap target when it acts. */
const SignInRow = ({
  label,
  labelTone = 'default',
  detail,
  pill,
  onClick,
}: {
  label: string
  labelTone?: 'default' | 'danger'
  detail?: ReactNode
  pill?: ReactNode
  onClick?: () => void
}) => {
  const body = (
    <>
      <span className="flex-1 min-w-0">
        <span
          className={`block text-sm font-semibold ${
            labelTone === 'danger' ? 'text-flag' : 'text-ink-900'
          }`}
        >
          {label}
        </span>
        {detail && (
          <span className="block text-xs text-ink-500 mt-0.5">{detail}</span>
        )}
      </span>
      {pill}
      {onClick && <IconChevRight size={16} className="text-ink-400" />}
    </>
  )
  const layout =
    'w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left border-t border-ink-100'
  return (
    <li>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={`${layout} hover:bg-ink-100/40 transition`}
        >
          {body}
        </button>
      ) : (
        <div className={layout}>{body}</div>
      )}
    </li>
  )
}

const Language = () => {
  const { t, i18n } = useTranslation()
  return (
    <Card padding="md" className="flex flex-col gap-3">
      <h2 className="font-display font-bold text-base text-ink-900">
        {t('profile.language.title')}
      </h2>
      <Field
        label={t('profile.language.label')}
        hint={t('profile.language.hint')}
      >
        <Select
          value={i18n.resolvedLanguage}
          onChange={(e) => i18n.changeLanguage(e.target.value)}
          options={SUPPORTED_LANGUAGES.map((lang) => ({
            value: lang,
            label: t(`profile.language.${lang}` as const),
          }))}
        />
      </Field>
    </Card>
  )
}

export default Profile
